import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { SOX_CYCLES_QUERY_KEY, processesQueryKey } from '../../queries/sox';
import {
  putSoxCycleStatus,
  putSoxCycleRiskScore,
  patchSoxCycleActiveStatus,
  patchSoxCycleArchive,
  patchSoxCycleUnarchive,
  putProcessStatus,
  patchProcessActiveStatus,
  deleteProcess,
  reorderCycleProcessesSequence
} from '../../request/sox';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

/**
 * Message for failed mutations: prefer API body, then Error#message (React Query / axios), else fallback.
 * @param {unknown} error
 * @param {string} fallback
 */
function formatMutationError(error, fallback) {
  if (error == null) return fallback;
  const d = error?.response?.data;
  const fromBody = d?.error ?? d?.message;
  if (typeof fromBody === 'string' && fromBody.trim()) return fromBody.trim();
  if (error instanceof Error && typeof error.message === 'string' && error.message.trim()) {
    return error.message.trim();
  }
  return fallback;
}

function processPatchKey(cycleListKey, processListKey) {
  return `${cycleListKey}|${processListKey}`;
}

function cycleStatusPatch(status) {
  return { status, workflow_status: status };
}

function cycleRiskScorePatch(riskScore) {
  return { risk_score: riskScore, riskScore };
}

function entityActivePatch(is_active) {
  return { is_active, active: is_active };
}

function entityArchivedPatch(archived) {
  return {
    is_archived: archived,
    archived,
    isArchived: archived
  };
}

function cycleRowMatchesListKey(c, cycleListKey) {
  return String(c.cycle_id ?? c.id) === String(cycleListKey);
}

/** Match cycle row by business list key or by UUID (mutations pass both). */
function cycleRowMatchesIdentifiers(c, cycleListKey, cycleUuid) {
  if (cycleListKey != null && cycleRowMatchesListKey(c, cycleListKey)) return true;
  if (cycleUuid != null && cycleUuid !== '') {
    const rowUuid = c.id ?? c.uuid ?? c.cycle_uuid;
    if (String(rowUuid ?? '') === String(cycleUuid)) return true;
  }
  return false;
}

function processRowMatchesListKey(p, processListKey) {
  return String(p.process_id ?? p.id) === String(processListKey);
}

/** In-place merge so list order stays the same (no getUnique refetch). */
function commitCyclePatchToCache(queryClient, cycleListKey, patch, cycleUuid) {
  // Prefix match so year-scoped ['sox-cycles', auditYearId] entries are patched too.
  queryClient.setQueriesData({ queryKey: SOX_CYCLES_QUERY_KEY }, (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((c) =>
      cycleRowMatchesIdentifiers(c, cycleListKey, cycleUuid) ? { ...c, ...patch } : c
    );
  });
}

function commitProcessPatchToCache(queryClient, cycleListKey, processListKey, patch) {
  // Prefix match so year-scoped [...processesQueryKey, auditYearId] entries are patched too.
  queryClient.setQueriesData({ queryKey: processesQueryKey(cycleListKey) }, (old) => {
    if (!Array.isArray(old)) return old;
    return old.map((p) =>
      processRowMatchesListKey(p, processListKey) ? { ...p, ...patch } : p
    );
  });
}

function removeProcessFromCache(queryClient, cycleListKey, processListKey, processUuid) {
  // Match ['processes', cycleId, auditYear] keys used by the dashboard queries.
  queryClient.setQueriesData({ queryKey: processesQueryKey(cycleListKey) }, (old) => {
    if (!Array.isArray(old)) return old;
    return old.filter(
      (p) =>
        String(p.process_id ?? p.id) !== String(processListKey) &&
        String(p.id ?? p.process_id) !== String(processUuid)
    );
  });
}

/** Only for delete process — refetches GET /api/getUniqueSOXCycles. */
function invalidateUniqueSoxCyclesQuery(queryClient) {
  return queryClient.invalidateQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
}

function buildCycleRowMutationOptions({
  queryClient,
  mergeCyclePatch,
  clearCyclePatch,
  mutationFn,
  cycleListKeyFrom,
  patchFromVars,
  successMessage,
  errorFallback,
  /** Refetch GET /api/getUniqueSOXCycles after success (archive/unarchive). */
  invalidateCycleListAfterSuccess = false
}) {
  return {
    mutationFn,
    onMutate: async (variables) => {
      const listKey = cycleListKeyFrom(variables);
      await queryClient.cancelQueries({ queryKey: SOX_CYCLES_QUERY_KEY });
      mergeCyclePatch(listKey, patchFromVars(variables));
    },
    onError: (err, variables) => {
      clearCyclePatch(cycleListKeyFrom(variables));
      toast.error(resolve403ErrorMessage(err, formatMutationError(err, errorFallback)));
    },
    onSuccess: async (_data, variables) => {
      const listKey = cycleListKeyFrom(variables);
      const patch = patchFromVars(variables);
      const cycleUuid = variables.cycleUuid;
      commitCyclePatchToCache(queryClient, listKey, patch, cycleUuid);
      clearCyclePatch(listKey);
      if (invalidateCycleListAfterSuccess) {
        await invalidateUniqueSoxCyclesQuery(queryClient);
      }
      toast.success(successMessage);
    }
  };
}

function buildProcessRowMutationOptions({
  queryClient,
  mergeProcessPatch,
  clearProcessPatch,
  mutationFn,
  cycleListKeyFrom,
  processListKeyFrom,
  patchFromVars,
  successMessage,
  errorFallback
}) {
  return {
    mutationFn,
    onMutate: async (variables) => {
      const listKey = cycleListKeyFrom(variables);
      await queryClient.cancelQueries({ queryKey: processesQueryKey(listKey) });
      mergeProcessPatch(
        listKey,
        processListKeyFrom(variables),
        patchFromVars(variables)
      );
    },
    onError: (err, variables) => {
      clearProcessPatch(cycleListKeyFrom(variables), processListKeyFrom(variables));
      toast.error(resolve403ErrorMessage(err, formatMutationError(err, errorFallback)));
    },
    onSuccess: (_data, variables) => {
      const ck = cycleListKeyFrom(variables);
      const pk = processListKeyFrom(variables);
      commitProcessPatchToCache(queryClient, ck, pk, patchFromVars(variables));
      clearProcessPatch(ck, pk);
      toast.success(successMessage);
    }
  };
}

export function useSoxDashboardMutations() {
  const queryClient = useQueryClient();
  const { selectedAuditYearId } = useAuditYear();
  const [cyclePatches, setCyclePatches] = useState({});
  const [processPatches, setProcessPatches] = useState({});

  const mergeCyclePatch = useCallback((cycleListKey, partial) => {
    setCyclePatches((s) => ({
      ...s,
      [cycleListKey]: { ...s[cycleListKey], ...partial }
    }));
  }, []);

  const clearCyclePatch = useCallback((cycleListKey) => {
    setCyclePatches((s) => {
      if (!(cycleListKey in s)) return s;
      const next = { ...s };
      delete next[cycleListKey];
      return next;
    });
  }, []);

  const mergeProcessPatch = useCallback((cycleListKey, processListKey, partial) => {
    const k = processPatchKey(cycleListKey, processListKey);
    setProcessPatches((s) => ({
      ...s,
      [k]: { ...s[k], ...partial }
    }));
  }, []);

  const clearProcessPatch = useCallback((cycleListKey, processListKey) => {
    const k = processPatchKey(cycleListKey, processListKey);
    setProcessPatches((s) => {
      if (!(k in s)) return s;
      const next = { ...s };
      delete next[k];
      return next;
    });
  }, []);

  const patchCycleWorkflowStatusMutation = useMutation(
    buildCycleRowMutationOptions({
      queryClient,
      mergeCyclePatch,
      clearCyclePatch,
      mutationFn: ({ cycleUuid, status }) => putSoxCycleStatus(cycleUuid, status),
      cycleListKeyFrom: (v) => v.cycleListKey,
      patchFromVars: ({ status }) => cycleStatusPatch(status),
      successMessage: 'Cycle workflow status updated',
      errorFallback: 'Could not update cycle workflow status.'
    })
  );

  const patchCycleRiskScoreMutation = useMutation(
    buildCycleRowMutationOptions({
      queryClient,
      mergeCyclePatch,
      clearCyclePatch,
      mutationFn: ({ cycleUuid, riskScore }) => putSoxCycleRiskScore(cycleUuid, riskScore),
      cycleListKeyFrom: (v) => v.cycleListKey,
      patchFromVars: ({ riskScore }) => cycleRiskScorePatch(riskScore),
      successMessage: 'Cycle risk score updated',
      errorFallback: 'Could not update cycle risk score.'
    })
  );

  const patchCycleActiveStatusMutation = useMutation(
    buildCycleRowMutationOptions({
      queryClient,
      mergeCyclePatch,
      clearCyclePatch,
      mutationFn: ({ cycleUuid, is_active }) => patchSoxCycleActiveStatus(cycleUuid, is_active),
      cycleListKeyFrom: (v) => v.cycleListKey,
      patchFromVars: ({ is_active }) => entityActivePatch(is_active),
      successMessage: 'Cycle active status updated',
      errorFallback: 'Could not update cycle active status.'
    })
  );

  const archiveCycleMutation = useMutation(
    buildCycleRowMutationOptions({
      queryClient,
      mergeCyclePatch,
      clearCyclePatch,
      mutationFn: ({ cycleUuid }) => patchSoxCycleArchive(cycleUuid),
      cycleListKeyFrom: (v) => v.cycleListKey,
      patchFromVars: () => entityArchivedPatch(true),
      successMessage: 'Cycle deleted',
      errorFallback: 'Could not delete cycle.',
      invalidateCycleListAfterSuccess: true
    })
  );

  const unarchiveCycleMutation = useMutation(
    buildCycleRowMutationOptions({
      queryClient,
      mergeCyclePatch,
      clearCyclePatch,
      mutationFn: ({ cycleUuid }) => patchSoxCycleUnarchive(cycleUuid),
      cycleListKeyFrom: (v) => v.cycleListKey,
      patchFromVars: () => entityArchivedPatch(false),
      successMessage: 'Cycle restored',
      errorFallback: 'Could not restore cycle.',
      invalidateCycleListAfterSuccess: true
    })
  );

  const patchProcessWorkflowStatusMutation = useMutation(
    buildProcessRowMutationOptions({
      queryClient,
      mergeProcessPatch,
      clearProcessPatch,
      mutationFn: ({ processUuid, status }) => putProcessStatus(processUuid, status),
      cycleListKeyFrom: (v) => v.cycleListKey,
      processListKeyFrom: (v) => v.processListKey,
      patchFromVars: ({ status }) => cycleStatusPatch(status),
      successMessage: 'Process workflow status updated',
      errorFallback: 'Could not update process workflow status.'
    })
  );

  const patchProcessActiveStatusMutation = useMutation(
    buildProcessRowMutationOptions({
      queryClient,
      mergeProcessPatch,
      clearProcessPatch,
      mutationFn: ({ processUuid, is_active }) => patchProcessActiveStatus(processUuid, is_active),
      cycleListKeyFrom: (v) => v.cycleListKey,
      processListKeyFrom: (v) => v.processListKey,
      patchFromVars: ({ is_active }) => entityActivePatch(is_active),
      successMessage: 'Process active status updated',
      errorFallback: 'Could not update process active status.'
    })
  );

  const deleteProcessMutation = useMutation({
    mutationFn: ({ processUuid }) => deleteProcess(processUuid),
    onMutate: async ({ cycleListKey }) => {
      await queryClient.cancelQueries({ queryKey: processesQueryKey(cycleListKey) });
    },
    onError: (err, { cycleListKey, processListKey }) => {
      clearProcessPatch(cycleListKey, processListKey);
      toast.error(resolve403ErrorMessage(err, formatMutationError(err, 'Could not delete process.')));
    },
    onSuccess: async (_data, { cycleListKey, processListKey, processUuid }) => {
      removeProcessFromCache(queryClient, cycleListKey, processListKey, processUuid);
      await invalidateUniqueSoxCyclesQuery(queryClient);
      clearProcessPatch(cycleListKey, processListKey);
      toast.success('Process deleted');
    }
  });

  const reorderCycleProcessesMutation = useMutation({
    mutationFn: ({ cycleListKey, processUuids }) =>
      reorderCycleProcessesSequence(cycleListKey, processUuids, selectedAuditYearId),
    onError: (err) => {
      toast.error(
        resolve403ErrorMessage(err, formatMutationError(err, 'Could not update process sequence.'))
      );
    },
    onSuccess: async (_data, { cycleListKey }) => {
      await queryClient.invalidateQueries({ queryKey: processesQueryKey(cycleListKey) });
      toast.success('Process sequence updated');
    }
  });

  return {
    queryClient,
    cyclePatches,
    processPatches,
    patchCycleWorkflowStatusMutation,
    patchCycleRiskScoreMutation,
    patchCycleActiveStatusMutation,
    archiveCycleMutation,
    unarchiveCycleMutation,
    patchProcessWorkflowStatusMutation,
    patchProcessActiveStatusMutation,
    deleteProcessMutation,
    reorderCycleProcessesMutation
  };
}
